import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Plus, Loader2, ArrowRight, Truck, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { subscribeJobs, seedAcsJob, createJob, getSiteSettings, saveSiteSettings } from "../lib/deliveries";

export default function AdminDeliveries() {
  const { toast } = useToast();
  const [jobs, setJobs] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [reviewUrl, setReviewUrl] = useState("");
  const [savingReview, setSavingReview] = useState(false);
  const [form, setForm] = useState({ job_name: "", client_name: "", job_date: "", pickup_address: "", contract_amount: "", driver_email: "" });

  useEffect(() => {
    // Auto-seed the ACS production job if it doesn't exist yet.
    seedAcsJob().catch((e) => console.warn("seed:", e?.message));
    getSiteSettings().then((s) => setReviewUrl(s?.google_review_url || ""));
    const unsub = subscribeJobs(setJobs);
    return () => unsub();
  }, []);

  const saveReview = async () => {
    setSavingReview(true);
    try { await saveSiteSettings({ google_review_url: reviewUrl.trim() }); toast({ title: "Review link saved", description: "Used on every customer delivery page." }); }
    catch (e) { toast({ title: "Couldn't save", description: e?.message }); }
    finally { setSavingReview(false); }
  };

  const reseed = async () => {
    setSeeding(true);
    try { await seedAcsJob(); toast({ title: "ACS job ready", description: "American Cancer Society route loaded." }); }
    catch (e) { toast({ title: "Seed failed", description: e?.message }); }
    finally { setSeeding(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.job_name) return;
    setCreating(true);
    try { await createJob(form); toast({ title: "Job created" }); setForm({ job_name: "", client_name: "", job_date: "", pickup_address: "", contract_amount: "", driver_email: "" }); }
    catch (err) { toast({ title: "Couldn't create", description: err?.message }); }
    finally { setCreating(false); }
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="bg-[#0f2847] text-white stripe-bg">
        <div className="container-518 py-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-1">Delivery management</div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold flex items-center gap-3"><Truck className="w-8 h-8 text-[#f59321]" /> Delivery jobs</h1>
          </div>
          <Button onClick={reseed} disabled={seeding} data-testid="seed-acs-job" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0f2847] rounded-full h-11 px-5 font-semibold">
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Load ACS Sep 30 job"}
          </Button>
        </div>
      </section>

      <section className="container-518 py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4" data-testid="jobs-list">
          {jobs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No jobs yet. The ACS job loads automatically — or create one.</p>
            </div>
          ) : jobs.map((j) => (
            <Link key={j.id} to={`/admin/deliveries/${j.id}`} data-testid={`job-card-${j.id}`} className="block bg-white rounded-2xl border border-slate-200 p-6 card-lift">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-bold text-[#f59321]">{j.job_reference}</div>
                  <h3 className="font-display font-extrabold text-[#0f2847] text-lg">{j.job_name}</h3>
                  <div className="text-sm text-slate-500 mt-0.5">{j.client_name} · {j.job_date}</div>
                </div>
                <span className="shrink-0 px-3 py-1 rounded-full text-xs font-bold bg-[#0f2847]/5 text-[#0f2847] border border-slate-200">{j.job_status}</span>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#f59321] rounded-full" style={{ width: `${j.total_stops ? (j.completed_stops / j.total_stops) * 100 : 0}%` }} />
                </div>
                <span className="text-sm font-bold text-[#0f2847]">{j.completed_stops || 0} / {j.total_stops || 0}</span>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>
            </Link>
          ))}
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 h-fit space-y-3" data-testid="create-job-form">
          <h3 className="font-display font-bold text-[#0f2847] flex items-center gap-2"><Plus className="w-4 h-4" /> New job</h3>
          {[["job_name", "Job name *"], ["client_name", "Client name"], ["job_date", "Job date (YYYY-MM-DD)"], ["pickup_address", "Pickup address"], ["contract_amount", "Contract amount"], ["driver_email", "Assign driver email"]].map(([k, label]) => (
            <div key={k}>
              <label className="text-xs font-semibold text-slate-600">{label}</label>
              <Input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="h-10 mt-1 rounded-lg border-slate-200" data-testid={`newjob-${k}`} />
            </div>
          ))}
          <Button type="submit" disabled={creating} className="w-full bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-11 font-semibold" data-testid="create-job-submit">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create job"}
          </Button>
        </form>
      </section>

      <section className="container-518 pb-10">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl" data-testid="customer-portal-settings">
          <h3 className="font-display font-bold text-[#0f2847] flex items-center gap-2"><Star className="w-4 h-4 text-[#f59321]" /> Customer portal settings</h3>
          <p className="text-xs text-slate-400 mt-1 mb-3">Google review link shown on every customer Delivery Proof page ("Leave us a review"). Editable here — no code changes needed.</p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[260px]">
              <label className="text-xs font-semibold text-slate-600">Google review URL</label>
              <Input value={reviewUrl} onChange={(e) => setReviewUrl(e.target.value)} placeholder="https://g.page/r/..../review" className="h-10 mt-1 rounded-lg border-slate-200" data-testid="review-url-input" />
            </div>
            <Button onClick={saveReview} disabled={savingReview} className="bg-[#0f2847] text-white rounded-full h-10 px-5 font-semibold" data-testid="review-url-save">
              {savingReview ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
